import { useState } from "react";
import { motion } from "framer-motion";
import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from "@/components/golf/golf-card";
import { GolfButton } from "@/components/golf/golf-button";
import { DrillStatusBadge, type DrillStatus } from "@/components/golf/drill-status-badge";
import { Filter, Plus, Play, CheckCircle } from "lucide-react";

interface Drill {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
  duration: string;
  status: DrillStatus;
}

const drills: Drill[] = [
  {
    id: "1",
    title: "Alignment Stick Drill",
    description: "Use alignment sticks to improve swing path and fix slice",
    category: "Swing Path",
    difficulty: "Beginner",
    duration: "10 min",
    status: "progress"
  },
  {
    id: "2", 
    title: "Slow Motion Swings",
    description: "Practice half-speed swings to improve tempo and balance",
    category: "Tempo",
    difficulty: "Beginner", 
    duration: "15 min",
    status: "new"
  },
  {
    id: "3",
    title: "Impact Position Hold",
    description: "Hold impact position to build muscle memory",
    category: "Impact",
    difficulty: "Intermediate",
    duration: "8 min",
    status: "done"
  },
  {
    id: "4",
    title: "3-Foot Putting Circle",
    description: "Make 10 putts in a row from 3 feet around the hole",
    category: "Putting",
    difficulty: "Beginner",
    duration: "12 min", 
    status: "new"
  }
];

export default function Practice() {
  const [selectedFilter, setSelectedFilter] = useState<string>("all");

  const filteredDrills = drills.filter(drill => {
    if (selectedFilter === "all") return true;
    return drill.status === selectedFilter;
  });

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* V2: Header with motion */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
        className="px-6 pt-8 pb-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-2">
              Practice Plan
            </h1>
            <p className="text-muted-foreground text-lg">
              Targeted drills for improvement
            </p>
          </div>
          <GolfButton 
            size="icon" 
            variant="outline"
            className="shadow-md hover:shadow-lg transition-all duration-350"
          >
            <Plus size={20} />
          </GolfButton>
        </div>
      </motion.div>

      {/* V2: Filter Tabs with motion */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: 0.1 }}
        className="px-6 mb-6"
      >
        <div className="flex items-center gap-2 overflow-x-auto">
          {[
            { id: "all", label: "All Drills" },
            { id: "new", label: "New" },
            { id: "progress", label: "In Progress" },
            { id: "done", label: "Completed" }
          ].map((filter) => (
            <motion.div key={filter.id} className="relative">
              <GolfButton
                variant={selectedFilter === filter.id ? "primary" : "outline"}
                size="sm"
                onClick={() => setSelectedFilter(filter.id)}
                className="whitespace-nowrap transition-all duration-350 shadow-sm hover:shadow-md"
              >
                {filter.label}
              </GolfButton>
              {selectedFilter === filter.id && (
                <motion.div
                  layoutId="practice-filter-indicator"
                  className="absolute -bottom-1 left-0 right-0 h-0.5 bg-fairway rounded-full"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* V2: Progress Overview with animated stats */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: 0.2 }}
        className="px-6 mb-6"
      >
        <GolfCard className="bg-gradient-to-r from-fairway/5 to-success/5 border-fairway/20 shadow-md">
          <GolfCardContent className="py-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.35 }}
              >
                <div className="text-2xl font-bold text-success">
                  {drills.filter(d => d.status === "done").length}
                </div>
                <div className="text-sm text-muted-foreground mt-1">Completed</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.35 }}
              >
                <div className="text-2xl font-bold text-warning">
                  {drills.filter(d => d.status === "progress").length}
                </div>
                <div className="text-sm text-muted-foreground mt-1">In Progress</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.35 }}
              >
                <div className="text-2xl font-bold text-info">
                  {drills.filter(d => d.status === "new").length}
                </div>
                <div className="text-sm text-muted-foreground mt-1">New</div>
              </motion.div>
            </div>
          </GolfCardContent>
        </GolfCard>
      </motion.div>

      {/* V2: Drill Cards with stagger animation */}
      <motion.div 
        className="px-6 space-y-4"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.08,
              delayChildren: 0.5
            }
          }
        }}
      >
        {filteredDrills.map((drill, index) => (
          <motion.div
            key={drill.id}
            variants={{
              hidden: { opacity: 0, x: -20 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] }
              }
            }}
          >
            <GolfCard className="shadow-md hover:shadow-lg transition-all duration-350 bg-surface-elevated">
              <GolfCardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <GolfCardTitle className="text-lg font-semibold">{drill.title}</GolfCardTitle>
                      <DrillStatusBadge status={drill.status} />
                    </div>
                    <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                      {drill.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="bg-muted px-3 py-1.5 rounded-md font-medium">{drill.category}</span>
                      <span className="font-medium">{drill.difficulty}</span>
                      <span className="font-medium">{drill.duration}</span>
                    </div>
                  </div>
                </div>
              </GolfCardHeader>
              <GolfCardContent>
                <div className="flex gap-3">
                  <GolfButton 
                    size="sm" 
                    className="flex-1 shadow-sm transition-all duration-350"
                    variant={drill.status === "done" ? "outline" : "primary"}
                  >
                    {drill.status === "done" ? (
                      <>
                        <CheckCircle size={16} className="mr-2" />
                        Completed
                      </>
                    ) : (
                      <>
                        <Play size={16} className="mr-2" />
                        {drill.status === "progress" ? "Continue" : "Start Drill"}
                      </>
                    )}
                  </GolfButton>
                  <GolfButton 
                    size="sm" 
                    variant="outline"
                    className="shadow-sm hover:shadow-md transition-all duration-350"
                  >
                    Details
                  </GolfButton>
                </div>
              </GolfCardContent>
            </GolfCard>
          </motion.div>
        ))}
      </motion.div>

      {filteredDrills.length === 0 && (
        <div className="px-6 text-center py-12">
          <div className="text-muted-foreground">
            <Filter size={48} className="mx-auto mb-4 opacity-50" />
            <p>No drills found for this filter.</p>
          </div>
        </div>
      )}
    </div>
  );
}